/**
 * Guide Me — v2 tree runner.
 *
 * Walks a parsed v2 Tour as a tree of steps. Block kinds (`if` /
 * `forEach` / `try`) recurse into their child arrays; decl kinds
 * (`let` / `set` / `capture`) mutate a frame-stack variable scope;
 * loop-control kinds (`break` / `continue` / `skip`) propagate as
 * structured signals up to the enclosing loop.
 *
 * Design notes:
 *
 * - **Variable scope is mutable; the Tour is not.** Author intent
 *   (the parsed `Tour`) stays deeply frozen. Variables, captures, and
 *   loop iterators live entirely on the runtime {@link VariableScope}
 *   stack inside the {@link ActionContext}.
 *
 * - **Leaf execution still goes through `executeStepWithLifecycle`.**
 *   The Phase 2 retry/backoff/AssertionError contract is preserved;
 *   the tree walker only adds structure on top.
 *
 * - **Pause / cancel / handoff** continue to flow through the
 *   `ctx.checkCancelled()` and `HandoffSignal` paths. The walker
 *   re-checks at every block boundary so cancellation is responsive
 *   even inside deeply nested constructs.
 *
 * - **Skip / back** UI affordances are not wired into the v2 tree
 *   walker (Phase 3 scope). Authors model expected failures with
 *   `try / catch / finally` instead. The v1 flat runLoop keeps its
 *   existing skip/back support.
 */

import { evalExpr, type EvalEnv } from '@empowernow/etl-tour';
import {
  ActionAbortError,
  AssertionError,
  CancellationError,
  HandoffSignal,
  executeStepWithLifecycle,
  type ActionContext,
} from './actions';
import { resolveAll, resolveOne } from './locators';
import { resolveAlias } from './locatorRegistry';
import type {
  CallStep,
  CaptureStep,
  Expr,
  ForEachStep,
  IfStep,
  LetStep,
  Locator,
  MacroDef,
  ParamDecl,
  Requirement,
  ScopeFilter,
  SetStep,
  Step,
  Tour,
  TryStep,
} from './types';

/* ─────────────────────────── pacing ──────────────────────────────── */

/**
 * Default post-step settle pause (ms) when the tour's `pace.settleMs`
 * and the per-step `settleMs` are both unset. Matches the v1 flat
 * run loop default in TourPlayer so v1 and v3 tours feel the same.
 */
const DEFAULT_SETTLE_MS = 1200;

/**
 * Step kinds that skip the post-step settle pause. `narrate` / `wait`
 * already control their own dwell time; `navigate` / `waitFor` are
 * utility steps the user doesn't need to "digest".
 */
const NO_SETTLE_TYPES = new Set<string>(['narrate', 'wait', 'navigate', 'waitFor']);

/* ─────────────────────────── variable scope ──────────────────────── */

/**
 * Frame-stack runtime variable scope. Each frame holds a small object
 * of name → value bindings; `get` walks frames inside-out. `set`
 * writes to the innermost frame.
 *
 * Top-level identifiers like `params`, `user`, etc. are NOT stored
 * here — they're routed through {@link makeEnv}'s `resolveTopLevel`
 * to a host-supplied set. This scope is exclusively for `vars` and
 * loop iterators.
 */
export class VariableScope {
  private readonly stack: Array<Record<string, unknown>> = [{}];

  get current(): Record<string, unknown> {
    return this.stack[this.stack.length - 1];
  }

  push(seed: Record<string, unknown> = {}): void {
    this.stack.push({ ...seed });
  }

  pop(): void {
    if (this.stack.length === 1) {
      throw new Error('VariableScope: cannot pop the global frame');
    }
    this.stack.pop();
  }

  /** Looks up `name`, walking frames inside-out. */
  get(name: string): unknown {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      if (Object.prototype.hasOwnProperty.call(this.stack[i], name)) {
        return this.stack[i][name];
      }
    }
    return undefined;
  }

  /** Writes `name = value` to the innermost frame. */
  set(name: string, value: unknown): void {
    this.current[name] = value;
  }

  /** Snapshot of the entire scope as a flat object — outer frames first. */
  snapshot(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const frame of this.stack) Object.assign(out, frame);
    return out;
  }
}

/* ─────────────────────────── EvalEnv adapter ─────────────────────── */

/**
 * Adapts a {@link VariableScope} (plus a small bag of host-supplied
 * top-level values) into the {@link EvalEnv} interface that the
 * expression evaluator consumes.
 *
 * Member access is allow-listed: only own enumerable properties of
 * plain objects (or the indexed elements of arrays / strings) are
 * exposed. Builtin function calls dispatch to a fixed table; builtins
 * that touch the DOM (`exists`, `visible`, `count`, `text`, `value`)
 * use the same locator engine the action handlers use.
 */
export interface RunnerHostScope {
  /**
   * Top-level identifier values for `params`, `user`, `tenant`,
   * `flags`, `env`, `route`. Any name not in `TOP_LEVEL_IDENTS`
   * resolves to `undefined`. Phase 4 wires `params` from the tour's
   * declarations and accepts host-supplied `user`/`tenant`/`flags`/
   * `env`/`route` so v2 expressions see real identity context.
   */
  topLevel?: Readonly<Record<string, unknown>>;
  /**
   * Optional host callback for permission checks consumed by both
   * `requires permission:` evaluation and the `can()` expression
   * builtin. Returns true when the current user is permitted.
   */
  checkPermission?: (action: string, resource?: string) => boolean;
  /**
   * Plugin id of the running tour, used to resolve `@:<name>`
   * (implicit-active-plugin) aliases through the locator registry.
   * Qualified `@plugin.<id>:<name>` aliases ignore this and look up
   * by the embedded plugin id.
   */
  activePluginId?: string;
}

function safeMemberAccess(parent: unknown, prop: string): unknown {
  if (parent == null) return undefined;
  if (Array.isArray(parent)) {
    if (prop === 'length') return parent.length;
    const idx = Number(prop);
    return Number.isInteger(idx) && idx >= 0 ? parent[idx] : undefined;
  }
  if (typeof parent === 'string') {
    if (prop === 'length') return parent.length;
    const idx = Number(prop);
    return Number.isInteger(idx) && idx >= 0 ? parent[idx] : undefined;
  }
  if (typeof parent === 'object') {
    // Only own enumerable properties. Never walks the prototype chain.
    if (Object.prototype.hasOwnProperty.call(parent, prop)) {
      return (parent as Record<string, unknown>)[prop];
    }
    return undefined;
  }
  return undefined;
}

/** Builds the EvalEnv used to evaluate expressions during tour execution. */
export function makeEnv(scope: VariableScope, host: RunnerHostScope = {}): EvalEnv {
  const topLevel = host.topLevel ?? {};
  const checkPermission = host.checkPermission;
  return {
    strict: false,
    resolveTopLevel: (name) => {
      if (name === 'vars') return scope.snapshot();
      if (Object.prototype.hasOwnProperty.call(topLevel, name)) return topLevel[name];
      return undefined;
    },
    resolveMember: safeMemberAccess,
    callBuiltin: (fn, args) => {
      switch (fn) {
        case 'length':   return String(args[0] ?? '').length;
        case 'lower':    return String(args[0] ?? '').toLowerCase();
        case 'upper':    return String(args[0] ?? '').toUpperCase();
        case 'contains': return String(args[0] ?? '').includes(String(args[1] ?? ''));
        case 'now':      return Date.now();
        case 'exists':   return !!resolveOne(args[0] as never);
        case 'visible':  return !!resolveOne(args[0] as never);
        case 'count':    return resolveAll(args[0] as never).length;
        case 'text': {
          const el = resolveOne(args[0] as never);
          return el ? (el.textContent ?? '').replace(/\s+/g, ' ').trim() : '';
        }
        case 'value': {
          const el = resolveOne(args[0] as never);
          if (!el) return '';
          if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
            return el.value;
          }
          return (el.textContent ?? '').trim();
        }
        case 'can': {
          if (!checkPermission) return false;
          const action = String(args[0] ?? '');
          const resource = args[1] === undefined ? undefined : String(args[1]);
          return checkPermission(action, resource);
        }
      }
    },
  };
}

/* ─────────────────────────── tree walker ─────────────────────────── */

type BlockSignal =
  | { kind: 'ok' }
  | { kind: 'break' }
  | { kind: 'continue' };

const OK: BlockSignal = { kind: 'ok' };

/**
 * Raised when a `call` references a macro that isn't defined or
 * recurses past the host's expansion depth limit. Treated by the
 * lifecycle wrapper as non-recoverable so retry can't accidentally
 * mask a structural authoring error.
 */
export class MacroError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MacroError';
  }
}

/**
 * Per-tour bag of macro definitions and the active expansion depth
 * counter. Threaded through the walker so any `call` step has access
 * to both. Created once at the top of `runTour` and never mutated.
 */
interface MacroEnv {
  readonly defs: ReadonlyMap<string, MacroDef>;
  readonly maxDepth: number;
  depth: number;
}

/**
 * Walks a step body. Returns a {@link BlockSignal} so loop-control
 * verbs propagate up to the enclosing `forEach` (the only thing that
 * cares). At the tour root any non-`ok` signal is a structural error
 * (loop-control verb outside a loop) and is caught in {@link runTour}.
 */
async function runBlock(
  ctx: ActionContext,
  body: ReadonlyArray<Step>,
  scope: VariableScope,
  host: RunnerHostScope,
  macros: MacroEnv,
): Promise<BlockSignal> {
  for (const step of body) {
    ctx.checkCancelled();
    const sig = await runStep(ctx, step, scope, host, macros);
    if (sig.kind !== 'ok') return sig;
  }
  return OK;
}

async function runStep(
  ctx: ActionContext,
  step: Step,
  scope: VariableScope,
  host: RunnerHostScope,
  macros: MacroEnv,
): Promise<BlockSignal> {
  // Centralised pre-resolution. Every step kind benefits from
  // template / varRef / alias rewrites — leaf steps go through the
  // lifecycle wrapper afterwards; non-leaf kinds (capture, let,
  // set) get the same view of the runtime context. The order
  // matters:
  //   1. templates  — `${…}` → string
  //   2. varRef     — `$name` → bound Locator (which may itself be an
  //                   alias-typed Locator from a `let X = @:foo`)
  //   3. alias      — `@:foo` / `@plugin.id:foo` → concrete strategy
  // Running alias AFTER varRef catches the chained
  // `let → $varRef → alias` case automatically.
  let resolved = resolveTemplates(step, scope, host);
  resolved = resolveLocatorVarRefs(resolved, scope);
  resolved = resolveLocatorAliases(resolved, host.activePluginId);

  switch (resolved.type) {
    case 'if':       return runIf(ctx, resolved, scope, host, macros);
    case 'forEach':  return runForEach(ctx, resolved, scope, host, macros);
    case 'try':      return runTry(ctx, resolved, scope, host, macros);
    case 'let':      return runLet(resolved, scope, host);
    case 'set':      return runSet(resolved, scope, host);
    case 'capture':  return runCapture(resolved, scope);
    case 'break':    return { kind: 'break' };
    case 'continue': return { kind: 'continue' };
    case 'skip':     return OK;
    case 'call':     return runCall(ctx, resolved, scope, host, macros);
    default: {
      await executeStepWithLifecycle(ctx, resolved);
      // Post-step settle pause — gives the user time to read the
      // narration bubble and see the result of the action before the
      // cursor moves on. Mirrors the v1 flat run loop in TourPlayer
      // (without this, v3 tours blaze through `move`/`scrollTo` steps
      // as soon as the cursor animation completes — too fast to read).
      // Skipped for step kinds that already manage their own dwell
      // time (narrate/wait) or that the user doesn't need to digest
      // (navigate/waitFor). Honoured at zero when the author sets
      // `settleMs: 0` explicitly.
      if (!NO_SETTLE_TYPES.has(resolved.type)) {
        const stepSettle = (resolved as { settleMs?: number }).settleMs;
        const settle =
          stepSettle !== undefined
            ? stepSettle
            : ctx.pace?.settleMs ?? DEFAULT_SETTLE_MS;
        if (settle > 0) await ctx.sleep(settle);
      }
      return OK;
    }
  }
}

/**
 * Dereferences `@plugin.<id>:<name>` and `@:<name>` aliases on a
 * step's locators by looking them up in the host
 * {@link locatorRegistry}. Aliases without a registered vocabulary
 * (or an unregistered name) throw {@link ActionAbortError} so the
 * tour author sees the typo immediately rather than silently
 * targeting nothing.
 *
 * `@:<name>` resolves against `activePluginId` (the tour's pluginId).
 * Steps with no alias-typed locators return untouched (zero-cost for
 * v1 tours and for v2 tours that still use raw testids).
 */
function resolveLocatorAliases(step: Step, activePluginId: string | undefined): Step {
  const dereferenceOne = (loc: Locator | undefined): Locator | undefined => {
    if (!loc || !loc.alias) return loc;
    const concrete = resolveAlias(loc.alias, activePluginId);
    if (!concrete) {
      const qualifier = loc.alias.plugin ? `@plugin.${loc.alias.plugin}:` : '@:';
      throw new ActionAbortError(
        `unknown alias ${qualifier}${loc.alias.name} — not registered in the locator vocabulary`,
      );
    }
    // Carry the original `nth` / `exact` / `within` modifiers onto
    // the resolved locator so authors can decorate aliases without
    // editing the vocabulary.
    const merged: Locator = { ...concrete };
    if (loc.exact !== undefined && merged.exact === undefined) merged.exact = loc.exact;
    if (loc.nth !== undefined && merged.nth === undefined) merged.nth = loc.nth;
    if (loc.within !== undefined && merged.within === undefined) {
      merged.within = dereferenceOne(loc.within);
    }
    return merged;
  };

  const stepHasLocator = (s: Step): s is Step & { locator: Locator } =>
    Object.prototype.hasOwnProperty.call(s, 'locator') &&
    (s as { locator?: unknown }).locator !== undefined;

  const out: Step = { ...step };
  if (stepHasLocator(step) && step.locator?.alias) {
    (out as { locator: Locator }).locator = dereferenceOne(step.locator) as Locator;
  } else if (stepHasLocator(step) && step.locator?.within?.alias) {
    // Cover the within-only-aliased case so chained scopes resolve.
    (out as { locator: Locator }).locator = {
      ...step.locator,
      within: dereferenceOne(step.locator.within) as Locator,
    };
  }
  if (step.type === 'drag') {
    if (step.from?.alias) (out as { from: Locator }).from = dereferenceOne(step.from) as Locator;
    if (step.to?.alias)   (out as { to: Locator }).to   = dereferenceOne(step.to)   as Locator;
  }
  if (step.type === 'multi-spot' && Array.isArray(step.locators)) {
    if (step.locators.some((l) => l?.alias)) {
      (out as unknown as { locators: Locator[] }).locators =
        step.locators.map((l) => dereferenceOne(l) as Locator);
    }
  }
  if (step.type === 'expect' && step.assertion?.locator?.alias) {
    (out as { assertion: typeof step.assertion }).assertion = {
      ...step.assertion,
      locator: dereferenceOne(step.assertion.locator) as Locator,
    };
  }
  return out;
}

/**
 * Dereferences `$varName` locator references on a step. Uses the
 * current scope to look up each `varRef`; the bound value must be a
 * Locator (typically established by `let varName = #testid`). When the
 * step has no varRefs, returns the input untouched (zero-overhead for
 * v1 tours).
 */
function resolveLocatorVarRefs(step: Step, scope: VariableScope): Step {
  const need = (loc: Locator | undefined): boolean => !!loc && !!loc.varRef;
  const stepHasLocator = (s: Step): s is Step & { locator: Locator } =>
    Object.prototype.hasOwnProperty.call(s, 'locator') && (s as { locator?: unknown }).locator !== undefined;

  const dereferenceOne = (loc: Locator | undefined): Locator | undefined => {
    if (!loc || !loc.varRef) return loc;
    const bound = scope.get(loc.varRef);
    // The author may have bound either a raw Locator (`let saveBtn =
    // #save`) or a LocatorExpr-style wrapper from the parser's
    // `let X = locator` form. Accept the parser's `{ locator: { kind:
    // 'step', locator: {...} } }` shape and unwrap it.
    if (bound && typeof bound === 'object') {
      if ('testid' in (bound as object) || 'role' in (bound as object) || 'label' in (bound as object) ||
          'text' in (bound as object) || 'selector' in (bound as object)) {
        return bound as Locator;
      }
      const wrapper = bound as { locator?: { kind?: string; locator?: Locator } };
      if (wrapper.locator?.kind === 'step' && wrapper.locator.locator) {
        return wrapper.locator.locator;
      }
    }
    throw new ActionAbortError(
      `locator reference $${loc.varRef} is not bound to a locator (let ${loc.varRef} = #testid)`,
    );
  };

  // Identify which fields might carry locators per step type.
  const out: Step = { ...step };
  if (stepHasLocator(step) && need(step.locator)) {
    (out as { locator: Locator }).locator = dereferenceOne(step.locator) as Locator;
  }
  if (step.type === 'drag') {
    if (need(step.from)) (out as { from: Locator }).from = dereferenceOne(step.from) as Locator;
    if (need(step.to))   (out as { to: Locator }).to   = dereferenceOne(step.to)   as Locator;
  }
  if (step.type === 'multi-spot' && Array.isArray(step.locators)) {
    (out as unknown as { locators: Locator[] }).locators = step.locators.map((l) => dereferenceOne(l) as Locator);
  }
  if (step.type === 'expect' && step.assertion?.locator && need(step.assertion.locator)) {
    (out as { assertion: typeof step.assertion }).assertion = {
      ...step.assertion,
      locator: dereferenceOne(step.assertion.locator) as Locator,
    };
  }
  return out;
}

/**
 * Returns a step copy with any compiled `narrateTpl` / `textTpl`
 * evaluated against the current scope. Steps without templates are
 * returned untouched (cheap path) so v1 tours are zero-overhead.
 */
function resolveTemplates(step: Step, scope: VariableScope, host: RunnerHostScope): Step {
  const hasNarrateTpl = step.narrateTpl !== undefined;
  const hasTextTpl =
    (step.type === 'narrate' || step.type === 'type') &&
    (step as { textTpl?: Expr }).textTpl !== undefined;
  if (!hasNarrateTpl && !hasTextTpl) return step;

  const env = makeEnv(scope, host);
  const out: Step = { ...step };
  if (hasNarrateTpl) {
    out.narrate = String(evalExpr(step.narrateTpl as Expr, env) ?? '');
  }
  if (hasTextTpl) {
    const tpl = (step as { textTpl?: Expr }).textTpl as Expr;
    const rendered = String(evalExpr(tpl, env) ?? '');
    if (out.type === 'narrate') (out as { text: string }).text = rendered;
    else if (out.type === 'type') (out as { text: string }).text = rendered;
  }
  return out;
}

function evalE(expr: Expr, scope: VariableScope, host: RunnerHostScope): unknown {
  return evalExpr(expr, makeEnv(scope, host));
}

function isTruthy(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v);
  if (typeof v === 'string') return v.length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/* ─── if / elseif / else ─── */

async function runIf(
  ctx: ActionContext,
  step: IfStep,
  scope: VariableScope,
  host: RunnerHostScope,
  macros: MacroEnv,
): Promise<BlockSignal> {
  for (const branch of step.branches) {
    if (isTruthy(evalE(branch.test, scope, host))) {
      return runBlock(ctx, branch.body, scope, host, macros);
    }
  }
  if (step.otherwise) return runBlock(ctx, step.otherwise, scope, host, macros);
  return OK;
}

/* ─── forEach ─── */

async function runForEach(
  ctx: ActionContext,
  step: ForEachStep,
  scope: VariableScope,
  host: RunnerHostScope,
  macros: MacroEnv,
): Promise<BlockSignal> {
  if (typeof step.source === 'object' && 'all' in step.source) {
    throw new ActionAbortError('forEach over locator-lists is not yet supported');
  }
  const value = evalE(step.source, scope, host);
  if (!Array.isArray(value)) {
    throw new ActionAbortError(
      `forEach source did not evaluate to an array (got ${typeof value})`,
    );
  }

  for (let i = 0; i < value.length; i++) {
    ctx.checkCancelled();
    scope.push({ [step.varName]: value[i], $index: i });
    try {
      const sig = await runBlock(ctx, step.body, scope, host, macros);
      if (sig.kind === 'break') return OK;
      if (sig.kind === 'continue') continue;
    } finally {
      scope.pop();
    }
  }
  return OK;
}

/* ─── try / catch / finally ─── */

async function runTry(
  ctx: ActionContext,
  step: TryStep,
  scope: VariableScope,
  host: RunnerHostScope,
  macros: MacroEnv,
): Promise<BlockSignal> {
  let bodySig: BlockSignal = OK;
  let pendingError: unknown = null;

  try {
    bodySig = await runBlock(ctx, step.body, scope, host, macros);
  } catch (err) {
    if (err instanceof CancellationError) throw err;
    if (err instanceof HandoffSignal) {
      if (step.finallyBody) await runBlock(ctx, step.finallyBody, scope, host, macros);
      throw err;
    }
    if (step.catchBody) {
      const errBinding = err instanceof Error
        ? { name: err.name, message: err.message }
        : { name: 'Error', message: String(err) };
      const seed = step.catchVar ? { [step.catchVar]: errBinding } : {};
      scope.push(seed);
      try {
        bodySig = await runBlock(ctx, step.catchBody, scope, host, macros);
      } finally {
        scope.pop();
      }
    } else {
      pendingError = err;
    }
  }

  if (step.finallyBody) {
    try {
      await runBlock(ctx, step.finallyBody, scope, host, macros);
    } catch (finErr) {
      if (finErr instanceof CancellationError || finErr instanceof HandoffSignal) throw finErr;
      pendingError = finErr;
    }
  }

  if (pendingError) throw pendingError;
  return bodySig;
}

/* ─── let / set / capture ─── */

function runLet(step: LetStep, scope: VariableScope, host: RunnerHostScope): BlockSignal {
  if (typeof step.value === 'object' && step.value !== null && 'locator' in step.value) {
    // Locator-binding form — Phase 3 doesn't yet resolve a LocatorExpr
    // (Phase 5 ships that). Bind the AST as-is so the value is
    // available for inspection but isn't yet usable as a `$varName`
    // locator.
    scope.set(step.name, step.value);
    return OK;
  }
  scope.set(step.name, evalE(step.value as Expr, scope, host));
  return OK;
}

function runSet(step: SetStep, scope: VariableScope, host: RunnerHostScope): BlockSignal {
  scope.set(step.name, evalE(step.value, scope, host));
  return OK;
}

function runCapture(step: CaptureStep, scope: VariableScope): BlockSignal {
  const what = step.what ?? 'text';
  if (what === 'count') {
    scope.set(step.as, resolveAll(step.locator).length);
    return OK;
  }
  const el = resolveOne(step.locator);
  if (!el) {
    throw new ActionAbortError(`capture: could not find ${describeForCapture(step.locator)}`);
  }
  if (what === 'text') {
    scope.set(step.as, (el.textContent ?? '').replace(/\s+/g, ' ').trim());
  } else {
    if (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement
    ) {
      scope.set(step.as, el.value);
    } else {
      scope.set(step.as, (el.textContent ?? '').trim());
    }
  }
  return OK;
}

/* ─── call (macro invocation) ─── */

async function runCall(
  ctx: ActionContext,
  step: CallStep,
  scope: VariableScope,
  host: RunnerHostScope,
  macros: MacroEnv,
): Promise<BlockSignal> {
  const def = macros.defs.get(step.macro);
  if (!def) {
    throw new MacroError(
      `unknown macro "${step.macro}" — not defined in this tour or any included library`,
    );
  }
  if (macros.depth + 1 > macros.maxDepth) {
    throw new MacroError(
      `macro expansion depth exceeded ${macros.maxDepth} (recursion?)`,
    );
  }

  // Evaluate args in the CALLER's scope+host so `${vars.x}` /
  // `${user.email}` resolve against the active context.
  const callerEnv = makeEnv(scope, host);
  const evaluated: Record<string, unknown> = {};
  for (const k of Object.keys(step.args)) {
    evaluated[k] = evalExpr(step.args[k], callerEnv);
  }

  // Apply macro-side defaults / required-but-missing checks.
  const resolvedParams = resolveParams(def.params, evaluated);

  // Build the macro's own host: top-level params override the caller's
  // params; the rest (user/tenant/flags/env/route, plus the active
  // plugin id for alias resolution) flow through so the macro body
  // can still see identity context and the same plugin vocabulary.
  const macroHost: RunnerHostScope = {
    topLevel: { ...(host.topLevel ?? {}), params: resolvedParams },
    checkPermission: host.checkPermission,
    activePluginId: host.activePluginId,
  };
  // Macros are LEXICALLY ISOLATED — fresh frame stack, no view of the
  // caller's `vars`. They communicate exclusively through args.
  const macroScope = new VariableScope();

  macros.depth++;
  try {
    const sig = await runBlock(ctx, def.body, macroScope, macroHost, macros);
    if (sig.kind === 'break' || sig.kind === 'continue') {
      throw new MacroError(
        `"${sig.kind}" leaked out of macro "${def.name}" — loop-control verbs may not exit a macro`,
      );
    }
  } finally {
    macros.depth--;
  }
  return OK;
}

function describeForCapture(loc: { testid?: string; label?: string; role?: string; name?: string; text?: string }): string {
  if (loc.testid) return `#${loc.testid}`;
  if (loc.label) return `@label:"${loc.label}"`;
  if (loc.text) return `@text:"${loc.text}"`;
  if (loc.role) return `@role.${loc.role}${loc.name ? `:"${loc.name}"` : ''}`;
  return JSON.stringify(loc);
}

/* ─────────────────────────── public API ──────────────────────────── */

export interface RunTourOptions {
  /**
   * Host-supplied top-level identifier values
   * (`params` / `user` / `tenant` / `flags` / `env` / `route`).
   * `params` declarations on the tour merge with caller-provided
   * `topLevel.params`: defaults fill in from the declarations;
   * required-but-missing throws before the body runs. Other axes
   * pass through unchanged for v2 expressions to see.
   */
  readonly topLevel?: Readonly<Record<string, unknown>>;
  /**
   * Permission check callback used by both `requires permission:`
   * gating and the `can()` expression builtin. Phase 4 hosts wire
   * this to their PDP / authz integration. Phase 3 callers can omit
   * it; gating fails closed in that case.
   */
  readonly checkPermission?: (action: string, resource?: string) => boolean;
}

/**
 * Resolves a tour's `params` declarations against caller-supplied
 * values. Defaults are filled in from declarations; required-but-
 * missing throws an {@link ActionAbortError} before the body runs.
 * Type coercion is intentionally light (matches the parser's coercion
 * for declared defaults).
 */
function resolveParams(
  decls: ReadonlyArray<ParamDecl> | undefined,
  supplied: Readonly<Record<string, unknown>> | undefined,
): Record<string, unknown> {
  if (!decls || decls.length === 0) return { ...(supplied ?? {}) };
  const out: Record<string, unknown> = {};
  for (const d of decls) {
    if (supplied && Object.prototype.hasOwnProperty.call(supplied, d.name)) {
      out[d.name] = supplied[d.name];
      continue;
    }
    if (d.default !== undefined) {
      out[d.name] = d.default;
      continue;
    }
    if (d.required) {
      throw new ActionAbortError(
        `tour requires param "${d.name}" but no value was supplied`,
      );
    }
    out[d.name] = undefined;
  }
  // Surface any extra caller-supplied values too so authors can pass
  // through ad-hoc context without a strict params declaration.
  if (supplied) {
    for (const k of Object.keys(supplied)) {
      if (!(k in out)) out[k] = supplied[k];
    }
  }
  return out;
}

/**
 * Evaluates a tour's `requires` / `onlyFor` / `notFor` clauses against
 * the resolved top-level scope. Returns the first failure reason or
 * null when the tour is permitted to run.
 *
 * - `role:X`        — true when `user.roles` includes `X`
 * - `permission:X`  — true when `checkPermission(X)` returns true
 * - `flag:X`        — true when `flags[X]` is truthy
 * - `plan:X`        — true when `tenant.plan === X`
 * - `env:a,b,c`     — true when `env` is in the comma-separated list
 *
 * `onlyFor`: tour runs only if EVERY axis matches.
 * `notFor`:  tour is denied if ANY axis matches.
 */
function evalGating(
  tour: Tour,
  topLevel: Readonly<Record<string, unknown>>,
  checkPermission?: (action: string, resource?: string) => boolean,
): string | null {
  const user = (topLevel.user ?? {}) as { roles?: string[]; permissions?: string[] };
  const tenant = (topLevel.tenant ?? {}) as { id?: string; plan?: string };
  const plugin = (topLevel.plugin ?? '') as string;
  const flags = (topLevel.flags ?? {}) as Record<string, boolean>;
  const envValue = (topLevel.env ?? '') as string;

  const requirementMet = (r: Requirement): boolean => {
    switch (r.kind) {
      case 'role':       return Array.isArray(user.roles) && user.roles.includes(r.value);
      case 'permission': return checkPermission ? !!checkPermission(r.value) : false;
      case 'flag':       return flags[r.value] === true;
      case 'plan':       return tenant.plan === r.value;
      case 'env': {
        const allowed = r.value.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
        return allowed.includes(envValue);
      }
    }
  };

  for (const r of tour.requires ?? []) {
    if (!requirementMet(r)) {
      return `requires ${r.kind}:"${r.value}"`;
    }
  }

  const matchesScope = (filter: ScopeFilter): { tenant: boolean; plugin: boolean; role: boolean } => ({
    tenant: !filter.tenant || filter.tenant.length === 0 ? true : !!tenant.id && filter.tenant.includes(tenant.id),
    plugin: !filter.plugin || filter.plugin.length === 0 ? true : !!plugin && filter.plugin.includes(plugin),
    role:   !filter.role   || filter.role.length === 0
      ? true
      : Array.isArray(user.roles) && user.roles.some((r) => filter.role!.includes(r)),
  });

  if (tour.onlyFor) {
    const m = matchesScope(tour.onlyFor);
    if (!m.tenant) return `only-for tenant: ${tour.onlyFor.tenant?.join(',') ?? ''}`;
    if (!m.plugin) return `only-for plugin: ${tour.onlyFor.plugin?.join(',') ?? ''}`;
    if (!m.role)   return `only-for role: ${tour.onlyFor.role?.join(',') ?? ''}`;
  }
  if (tour.notFor) {
    if (tour.notFor.tenant && tour.notFor.tenant.length > 0 && tenant.id && tour.notFor.tenant.includes(tenant.id)) {
      return `not-for tenant: ${tenant.id}`;
    }
    if (tour.notFor.plugin && tour.notFor.plugin.length > 0 && plugin && tour.notFor.plugin.includes(plugin)) {
      return `not-for plugin: ${plugin}`;
    }
    if (tour.notFor.role && tour.notFor.role.length > 0 && Array.isArray(user.roles)
        && user.roles.some((r) => tour.notFor!.role!.includes(r))) {
      return `not-for role`;
    }
  }
  return null;
}

/**
 * Runs a v2 tour as a tree walk. Returns when the body completes
 * normally, throws on cancellation / handoff / unrecovered error.
 *
 * v1 tours (no nested blocks) work identically through this entry
 * point — the walker degrades to a flat sequence — but the existing
 * v1 flat runLoop in {@link TourPlayer} keeps its skip/back UI
 * affordances, so callers with v1 tours should stick with the flat
 * runLoop.
 */
export async function runTour(
  ctx: ActionContext,
  tour: Tour,
  options: RunTourOptions = {},
): Promise<void> {
  // Library-only files have no body — they're macro packs included by
  // other tours. Nothing to execute.
  if (tour.isLibrary === true) return;

  // Resolve params: defaults fill in, required-but-missing throws.
  const suppliedTop = options.topLevel ?? {};
  const resolvedParams = resolveParams(
    tour.params,
    (suppliedTop.params ?? undefined) as Record<string, unknown> | undefined,
  );
  const topLevel: Record<string, unknown> = {
    ...suppliedTop,
    params: resolvedParams,
  };

  // Header gating: refuse to start if any requires/only-for/not-for
  // clause is unsatisfied. Surfaced as a HandoffSignal so TourPlayer
  // shows a clean handoff message rather than an aborted state.
  const denialReason = evalGating(tour, topLevel, options.checkPermission);
  if (denialReason !== null) {
    throw new HandoffSignal(`This tour is gated: ${denialReason}`);
  }

  const scope = new VariableScope();
  const host: RunnerHostScope = {
    topLevel,
    checkPermission: options.checkPermission,
    // Threaded so `@:<name>` aliases resolve against the tour's plugin.
    activePluginId: tour.pluginId,
  };
  const macroDefs = new Map<string, MacroDef>();
  if (tour.macros) {
    for (const k of Object.keys(tour.macros)) {
      macroDefs.set(k, tour.macros[k]);
    }
  }
  const macros: MacroEnv = {
    defs: macroDefs,
    maxDepth: 8,
    depth: 0,
  };
  const sig = await runBlock(ctx, tour.steps, scope, host, macros);
  if (sig.kind === 'break' || sig.kind === 'continue') {
    throw new ActionAbortError(
      `"${sig.kind}" used outside any forEach loop`,
    );
  }
}
